import React, { useEffect } from 'react';
import { Button } from '@material-ui/core';
import { Alert, AlertTitle } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useMeContext } from '~/modules/me/useMeContext';
import { useDialogState, useIsBreakpointDown } from '~/modules/common/hooks';
import { hasPermission } from '~/modules/common/permissions/useHasPermission';
import { DelaStarIcon } from '~/modules/common/components/Icons';
import useResumeParsingProcessingSubscription from './useResumeParsingProcessingSubscription';
import useRefetchAttachmentInfo from './useRefetchAttachmentInfo';
import useStyles, { useIconStyles } from './useStyles';

const UserResumeParsingProcessedAlert = () => {
  const classes = useStyles();
  const iconClasses = useIconStyles();
  const {
    id: userUri,
    userAccessRoles,
    permissionsMap,
    featureFlags
  } = useMeContext();
  const { refetchAttachmentInfo } = useRefetchAttachmentInfo();
  const isMobile = useIsBreakpointDown('sm');
  const {
    id: resumeUserUri,
    userSlug
  } = useResumeParsingProcessingSubscription({
    userUri,
    skipSubscription:
      !featureFlags.isPsaPraaUserProfileNudgesEnabled ||
      isMobile ||
      !(
        (userAccessRoles.some(
          r => r === 'urn:replicon:user-access-role:resource-manager'
        ) &&
          hasPermission(permissionsMap)({
            actionUri: 'urn:replicon:user-action:view-user',
            dataAccessLevelUri:
              'urn:replicon:user-data-access-level:resource-manager'
          })) ||
        userAccessRoles.some(
          r => r === 'urn:replicon:user-access-role:project-resource'
        )
      )
  });

  const { open, closeDialog, openDialog } = useDialogState();

  useEffect(() => {
    if (userSlug) {
      openDialog();
      refetchAttachmentInfo();
    }
  }, [refetchAttachmentInfo, openDialog, userSlug]);

  const history = useHistory();
  const location = useLocation();

  const isResumeForCurrentUser = resumeUserUri === userUri;
  const viewSuggestionsLink = isResumeForCurrentUser
    ? '/profile'
    : `/resource-pool/${userSlug}`;

  const handleViewSuggestions = () => {
    if (location.pathname === viewSuggestionsLink) {
      window.location.reload();
    } else {
      history.push(viewSuggestionsLink);
    }
  };

  const iconComponent = <DelaStarIcon role="img" classes={iconClasses} />;

  if (open) {
    return (
      <Alert
        severity="info"
        onClose={closeDialog}
        classes={classes}
        icon={iconComponent}
      >
        <AlertTitle>
          <FormattedMessage id="userResumeParsingProcessedAlert.userProfile" />
        </AlertTitle>
        <div className={classes.container}>
          <FormattedMessage id="userResumeParsingProcessedAlert.description" />
          <Button
            onClick={handleViewSuggestions}
            color="primary"
            className={classes.button}
          >
            <FormattedMessage id="userResumeParsingProcessedAlert.viewSuggestions" />
          </Button>
        </div>
      </Alert>
    );
  }

  return <></>;
};

export default UserResumeParsingProcessedAlert;
